// The one QR roll for a site, the same on both print cards (ASSESSMENT_FLOW_B5_WORDS.md, part 1b; mockup
// MOCKUP_ASSESSMENT_FLOW.html screen 5, approved by Robert 2026-10-29 6:59 AM).
//
// Drive holds one QR PDF per site, 00_QR_Labels.pdf. Field Labels and the Assessment Sheet both print it, so both
// build it from this one list: every live asset on the sheet, plus every Asset ID the walk sets that is not on the
// sheet yet, room by room in ID order. Whichever card updates the PDF, it holds the same labels.
//
// What printed: an asset on the sheet keeps qrLabelPrintedAt. A QR label printed from either card is also marked in
// the walk (printed, key "qr|<Asset ID>"), so a label printed before its row is saved still reads as printed. The
// sheet carries those marks onto its saved rows the next time it opens (asset-id-page.ts).
// Pure. Unit tested: tests/unit/qr-roll.test.ts.

import { assetIdOf, listRoomOf, shockLabelOnly, type ClassOption } from "./asset-id";
import { bringIn } from "./bring-in";
import type { FieldSite } from "./field-labels-site";
import { masterName } from "./drive-folders";
import { FOOTER_LINE1, QR_PAGE } from "./qr-label-pdf";
import { driverName, QR_CENTERED_SINCE_MS } from "./driver-page";
import type { PcRoll } from "./print-card";
import { esc } from "./training-screens";

/** The walk's printed key for a QR label. Field labels use their print ID; QR labels their Asset ID. */
export const qrPrintKey = (assetId: string): string => `qr|${assetId}`;

export interface QrEntry {
  id: string;
  /** "ER1", "EA3", "Outside": the room the label is grouped under. */
  room: string;
  /** When it last printed, from the sheet or the walk. "" never. */
  printedAt: string;
  /** On the sheet: when the row was made. */
  addedAt: string;
  onSheet: boolean;
  /** A workbook asset whose label the label script printed before SafeTag: never NEW. */
  scriptPrinted: boolean;
}

type Docs = Record<string, Record<string, unknown>>;

/** The walk's Asset IDs that get a QR label: housings and buckets, no panel breakers, no Shock Only gear. */
export function walkQrIds(walk: FieldSite | null, siteNumber: string, classes: readonly ClassOption[]): string[] {
  if (!walk) return [];
  // Bring In makes exactly the sheet's rows. Shock Only gear comes in with its voltage set: no QR label for it.
  return bringIn(walk, siteNumber, classes, new Set())
    .rows.filter((f) => f.volt !== "120V (1-P)")
    .map((f) => assetIdOf(siteNumber, f, classes))
    .filter(Boolean);
}

export function qrEntries(o: { walk: FieldSite | null; siteNumber: string; classes: readonly ClassOption[]; docs: Docs }): QrEntry[] {
  const out = new Map<string, QrEntry>();
  const walkMark = (id: string) => o.walk?.printed?.[qrPrintKey(id)]?.at ?? "";
  for (const [id, d] of Object.entries(o.docs)) {
    // Over 50 V and under 208 V takes a shock label, not a QR label (Robert, 2026-10-28 10:46 AM).
    if (d.isRemoved === true || shockLabelOnly(d)) continue;
    const printedAt = String(d.qrLabelPrintedAt ?? "") || walkMark(id);
    out.set(id, { id, room: listRoomOf({ assetId: id, location: String(d.location ?? "") }), printedAt, addedAt: String(d.createdAt ?? ""), onSheet: true, scriptPrinted: d.source !== "Asset ID page" });
  }
  for (const id of walkQrIds(o.walk, o.siteNumber, o.classes)) {
    // Removed on the sheet: the sheet is the truth (6:26 AM). It does not come back from the walk.
    if (out.has(id) || o.docs[id]) continue;
    out.set(id, { id, room: listRoomOf({ assetId: id, location: "" }), printedAt: walkMark(id), addedAt: "", onSheet: false, scriptPrinted: false });
  }
  return [...out.values()].sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }));
}

/** NEW: not in the Drive PDF yet. A QR label shows only its ID, so it never reads CHANGED: a new ID is a new label. */
export const qrIsNew = (e: QrEntry): boolean => !e.printedAt && !e.scriptPrinted;

/** The QR label drawn small: the QR, the Asset ID, the footer. */
export function qrArt(id: string): string {
  return `<span class="pc-art qr"><span class="pc-qrsq"></span><span class="pc-qrt"><b>${esc(id)}</b><i>${esc(FOOTER_LINE1)}</i></span></span>`;
}

const day = (iso: string): string => {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
};

/** The one roll, the same on both cards. title: the field card names it, the sheet's card has only this roll. */
export function qrRollOf(entries: readonly QrEntry[], siteNumber: string, title = "2.25 × 1.0 in"): PcRoll {
  return {
    key: "qr",
    title,
    sub: `set the driver to ${driverName(QR_PAGE)}`,
    driveName: masterName("qr", siteNumber),
    pageChange: { sinceMs: QR_CENTERED_SINCE_MS, note: `The label now prints centered on ${driverName(QR_PAGE)}.` },
    folder: "02 QR Labels",
    withRooms: true,
    items: entries.map((e) => {
      const fresh = qrIsNew(e);
      return {
        key: e.id,
        room: e.room,
        state: fresh ? ("new" as const) : ("current" as const),
        why: fresh ? (e.onSheet ? (day(e.addedAt) ? `Added ${day(e.addedAt)}` : "Not printed yet") : "Not printed yet · not pushed yet") : "",
        reprintWhy: e.printedAt ? `Last printed ${day(e.printedAt)}` : "Printed by the label script",
        art: qrArt(e.id),
      };
    }),
  };
}
